export const PERMISSIONS = {
  DASHBOARD_READ: 'dashboard:read',

  ORDERS_READ: 'orders:read',
  ORDERS_WRITE: 'orders:write',
  ORDERS_DELETE: 'orders:delete',
  ORDERS_COMPLETE: 'orders:complete',

  CUSTOMERS_READ: 'customers:read',
  CUSTOMERS_WRITE: 'customers:write',

  PRODUCTS_READ: 'products:read',
  PRODUCTS_WRITE: 'products:write',

  PACKAGES_READ: 'packages:read',
  PACKAGES_WRITE: 'packages:write',

  MEDIA_READ: 'media:read',
  MEDIA_WRITE: 'media:write',

  ESTATES_READ: 'estates:read',
  ESTATES_WRITE: 'estates:write',
  ESTATES_DASHBOARD_READ: 'estates:dashboard:read',
  ESTATES_REPORT_READ: 'estates:report:read',

  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',

  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',

  BROADCAST_SEND: 'broadcast:send',

  REFERRALS_READ: 'referrals:read',
  REFERRALS_WRITE: 'referrals:write',

  MERCHANTS_READ: 'merchants:read',
  MERCHANTS_MANAGE: 'merchants:manage',

  CONVERSATIONS_READ: 'conversations:read',
  CONVERSATIONS_SEND: 'conversations:send',

  WHATSAPP_MANAGE: 'whatsapp:manage',

  AUDIT_READ: 'audit:read',

  CMS_READ: 'cms:read',
  CMS_WRITE: 'cms:write',

  AHJIM_READ: 'ahjim:read',
  AHJIM_WRITE: 'ahjim:write',

  CUSTOMER_JOURNEYS_MANAGE: 'customer_journeys:manage',

  SUPPLIER_ORDERS_READ: 'supplier_orders:read',
  SUPPLIER_ANALYTICS_READ: 'supplier_analytics:read',

  ANALYTICS_READ: 'analytics:read',

  IMPACT_READ: 'impact:read',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  super_admin: Object.values(PERMISSIONS),
  admin: [
    PERMISSIONS.DASHBOARD_READ,
    PERMISSIONS.ORDERS_READ,
    PERMISSIONS.ORDERS_WRITE,
    PERMISSIONS.ORDERS_DELETE,
    PERMISSIONS.ORDERS_COMPLETE,
    PERMISSIONS.CUSTOMERS_READ,
    PERMISSIONS.CUSTOMERS_WRITE,
    PERMISSIONS.PRODUCTS_READ,
    PERMISSIONS.PRODUCTS_WRITE,
    PERMISSIONS.PACKAGES_READ,
    PERMISSIONS.PACKAGES_WRITE,
    PERMISSIONS.MEDIA_READ,
    PERMISSIONS.MEDIA_WRITE,
    PERMISSIONS.ESTATES_READ,
    PERMISSIONS.ESTATES_DASHBOARD_READ,
    PERMISSIONS.ESTATES_REPORT_READ,
    PERMISSIONS.REFERRALS_READ,
    PERMISSIONS.REFERRALS_WRITE,
    PERMISSIONS.CONVERSATIONS_READ,
    PERMISSIONS.CONVERSATIONS_SEND,
    PERMISSIONS.WHATSAPP_MANAGE,
    PERMISSIONS.CMS_READ,
    PERMISSIONS.CMS_WRITE,
    PERMISSIONS.AHJIM_READ,
    PERMISSIONS.AHJIM_WRITE,
    PERMISSIONS.ANALYTICS_READ,
    PERMISSIONS.MERCHANTS_MANAGE,
    PERMISSIONS.IMPACT_READ,
  ],
  supplier: [
    PERMISSIONS.PRODUCTS_READ,
    PERMISSIONS.PRODUCTS_WRITE,
    PERMISSIONS.MEDIA_READ,
    PERMISSIONS.MEDIA_WRITE,
    PERMISSIONS.SUPPLIER_ORDERS_READ,
    PERMISSIONS.SUPPLIER_ANALYTICS_READ,
  ],
  estate_manager: [
    PERMISSIONS.ORDERS_READ,
    PERMISSIONS.ESTATES_DASHBOARD_READ,
    PERMISSIONS.ESTATES_REPORT_READ,
  ],
  merchant: [
    PERMISSIONS.ORDERS_READ,
    PERMISSIONS.MERCHANTS_READ,
    PERMISSIONS.MERCHANTS_MANAGE,
  ],
};

export interface PermissionUser {
  id: number;
  username: string;
  role: string;
  display_name?: string;
  permissions?: Permission[] | string[];
  estateIds?: number[];
}

export function getRolePermissions(role?: string): Permission[] {
  return (ROLE_PERMISSIONS[role || ''] || []).slice();
}

export function normalizePermissions(user: PermissionUser | null): Permission[] {
  if (!user) return [];
  if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    return user.permissions as Permission[];
  }
  return getRolePermissions(user.role);
}

export function hasPermission(
  user: PermissionUser | null,
  permission: Permission | string
): boolean {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  const perms = normalizePermissions(user);
  return perms.includes(permission as Permission);
}

export function hasAnyPermission(
  user: PermissionUser | null,
  permissions: (Permission | string)[]
): boolean {
  return permissions.some((p) => hasPermission(user, p));
}
